import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { imgSrc, imgProps } from "../../lib/api";
import { useLang, whatsappLink } from "../../i18n";
import { MaskedLine, Eyebrow, EASE } from "../Reveal";
import { WhatsAppIcon } from "../WhatsAppButton";

function HeroMedia({ video, poster }) {
  if (video) {
    return (
      <video
        data-testid="hero-video"
        src={video}
        poster={imgSrc(poster)}
        autoPlay
        muted
        loop
        playsInline
        className="h-[115%] w-full object-cover"
      />
    );
  }
  return (
    <motion.img
      data-testid="hero-image"
      {...imgProps(poster)}
      alt=""
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 8, ease: "easeOut" }}
      className="h-[115%] w-full object-cover"
    />
  );
}

export default function HeroSection({ heroVideo, heroPoster }) {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 240]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.1]);
  return (
    <>
      {/* HERO */}
      <section className="relative isolate h-[100svh] min-h-[640px] flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <HeroMedia video={heroVideo} poster={heroPoster} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-[#080808]/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/85 via-[#080808]/35 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-14 sm:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}>
            <Eyebrow data-testid="hero-eyebrow">{t.hero_eyebrow}</Eyebrow>
          </motion.div>
          <h1 className="mt-6 max-w-5xl font-serif font-light tracking-tight leading-[1.02] text-[#F3EFE6] text-[11vw] sm:text-[7.5vw] lg:text-[5.4vw]">
            <MaskedLine delay={0.35}>{t.hero_line1}</MaskedLine>
            <MaskedLine delay={0.5}>{t.hero_line2}</MaskedLine>
            {t.hero_line3 && <MaskedLine delay={0.65} className="italic text-[#C8A86B]">{t.hero_line3}</MaskedLine>}
          </h1>
          <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.0, ease: EASE }} className="max-w-md text-sm sm:text-base font-light text-[#A39E93] leading-relaxed">
              {t.hero_sub}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.15, ease: EASE }} className="flex flex-wrap gap-4">
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-contact" className="group inline-flex items-center gap-3 bg-[#C8A86B] px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-[#080808] transition-colors duration-300 hover:bg-[#D8BD85]">
                <WhatsAppIcon size={16} /> {t.hero_cta_contact}
              </a>
              <Link to="/projects" data-testid="hero-cta-projects" className="group inline-flex items-center gap-3 border border-[#F3EFE6]/25 px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-[#F3EFE6] transition-colors duration-300 hover:border-[#C8A86B] hover:text-[#C8A86B]">
                {t.hero_cta_projects}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3">
          <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#827D72] [writing-mode:vertical-rl]">{t.scroll_hint}</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="h-10 w-[1px] bg-gradient-to-b from-[#C8A86B] to-transparent" />
        </motion.div>
      </section>

    </>
  );
}
